import { useEffect, useMemo, useState } from 'react';
import {
  useAttendanceToday,
  useAttendanceLogin,
  useAttendanceBreak,
  useAttendanceResume,
  useAttendanceLogout,
} from '../../hooks/useAttendance';
import { Button } from '../ui/Button';
import { formatClock } from '../../lib/utils';

// No per-user shift-policy field exists yet — a fixed 8h goal is the same
// simplification the original design prototype made. Swap for a real value
// once shift policies exist.
const SHIFT_HOURS = 8;
const GOAL_SECONDS = SHIFT_HOURS * 3600;

const RING_RADIUS = 140;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function WorkTimerCard({ firstClockInAt }: { firstClockInAt?: string | null }) {
  const { data: today, isLoading } = useAttendanceToday();
  const login = useAttendanceLogin();
  const startBreak = useAttendanceBreak();
  const resume = useAttendanceResume();
  const logout = useAttendanceLogout();

  // Ticks every second locally; re-derives from the last-synced server
  // snapshot each tick so the 30s refetch in useAttendanceToday
  // self-corrects drift instead of the display running on a naive counter.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const [syncedAt, setSyncedAt] = useState(() => Date.now());
  useEffect(() => {
    setSyncedAt(Date.now());
  }, [today]);

  const status = today?.status ?? 'not_started';
  const currentType = today?.currentSessionType ?? null;
  const sessionStartMs = today?.currentSessionStartedAt
    ? new Date(today.currentSessionStartedAt).getTime()
    : null;

  const liveSeconds = sessionStartMs ? Math.floor((now - sessionStartMs) / 1000) : 0;
  const syncedLiveSeconds = sessionStartMs ? Math.floor((syncedAt - sessionStartMs) / 1000) : 0;

  const workSecondsToday = today?.workSecondsToday ?? 0;
  const breakSecondsToday = today?.breakSecondsToday ?? 0;

  const work =
    currentType === 'work'
      ? Math.max(0, workSecondsToday - syncedLiveSeconds) + liveSeconds
      : workSecondsToday;
  const brk =
    currentType === 'break'
      ? Math.max(0, breakSecondsToday - syncedLiveSeconds) + liveSeconds
      : breakSecondsToday;

  const workFraction = Math.min(work / GOAL_SECONDS, 1);
  const breakFraction = Math.min((work + brk) / GOAL_SECONDS, 1);
  const workOffset = RING_CIRCUMFERENCE * (1 - workFraction);
  const breakOffset = RING_CIRCUMFERENCE * (1 - breakFraction);
  const percentLabel = `${Math.round(workFraction * 100)}%`;
  const remainingSeconds = Math.max(GOAL_SECONDS - work, 0);

  const isPending = login.isPending || startBreak.isPending || resume.isPending || logout.isPending;
  const actionError = login.error || startBreak.error || resume.error || logout.error;
  const errorMessage = (actionError as { response?: { data?: { message?: string } } } | null)
    ?.response?.data?.message;

  const clockedInLabel = useMemo(() => {
    if (!firstClockInAt) return '--:--';
    return new Date(firstClockInAt).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }, [firstClockInAt]);

  return (
    <div className="bg-white border border-[#e8eaf2] rounded-[22px] p-[30px] flex flex-col items-center gap-[22px] shadow-[0_1px_2px_rgba(13,18,32,.04)]">
      <div className="self-stretch flex justify-between items-center">
        <div className="text-[15px] font-extrabold text-[#0d1220]">Work timer</div>
        <div className="text-xs font-semibold text-[#8b93a7]">
          Shift goal {SHIFT_HOURS}h 00m
        </div>
      </div>

      <div className="relative w-full max-w-[320px] aspect-square grid place-items-center">
        <svg
          viewBox="0 0 320 320"
          className="absolute inset-0 w-full h-full"
          style={{ transform: 'rotate(-90deg)' }}
        >
          <circle cx="160" cy="160" r={RING_RADIUS} fill="none" stroke="#eef0f7" strokeWidth="18" />
          <circle
            cx="160"
            cy="160"
            r={RING_RADIUS}
            fill="none"
            stroke="#7b3dff"
            strokeWidth="18"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={breakOffset}
            opacity="0.35"
          />
          <circle
            cx="160"
            cy="160"
            r={RING_RADIUS}
            fill="none"
            stroke="#2f5cff"
            strokeWidth="18"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={workOffset}
          />
        </svg>
        <div className="text-center flex flex-col gap-1.5">
          <div className="text-[11px] font-bold tracking-[1.6px] text-[#8b93a7]">WORK TIME</div>
          <div
            className="font-mono font-medium leading-none whitespace-nowrap text-[#0d1220]"
            style={{ fontSize: 'clamp(32px,7vw,52px)', letterSpacing: '-2px' }}
          >
            {formatClock(work)}
          </div>
          <div className="text-[13px] font-bold text-[#2f5cff]">{percentLabel} of shift</div>
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap justify-center">
        {(status === 'not_started' || status === 'ended') && (
          <Button
            disabled={isLoading || isPending}
            onClick={() => login.mutate()}
            className="rounded-full px-10 py-[15px] h-auto bg-[#2f5cff] hover:bg-[#1d3fc4] text-white text-[15px] font-bold shadow-[0_8px_22px_rgba(47,92,255,.28)]"
          >
            {login.isPending ? 'Starting...' : 'Start work'}
          </Button>
        )}
        {status === 'working' && (
          <>
            <Button
              disabled={isPending}
              onClick={() => logout.mutate()}
              className="rounded-full px-10 py-[15px] h-auto bg-[#2f5cff] hover:bg-[#1d3fc4] text-white text-[15px] font-bold shadow-[0_8px_22px_rgba(47,92,255,.28)]"
            >
              {logout.isPending ? 'Ending...' : 'End work'}
            </Button>
            <Button
              variant="outline"
              disabled={isPending}
              onClick={() => startBreak.mutate()}
              className="rounded-full px-[26px] py-[15px] h-auto border-[#e2e5ef] text-[#3a4256] text-[15px] font-bold hover:bg-[#f6f7fb] shadow-none"
            >
              {startBreak.isPending ? 'Starting break...' : 'Take a break'}
            </Button>
          </>
        )}
        {status === 'on_break' && (
          <>
            <Button
              disabled={isPending}
              onClick={() => resume.mutate()}
              className="rounded-full px-10 py-[15px] h-auto bg-[#2f5cff] hover:bg-[#1d3fc4] text-white text-[15px] font-bold shadow-[0_8px_22px_rgba(47,92,255,.28)]"
            >
              {resume.isPending ? 'Resuming...' : 'Resume work'}
            </Button>
            <Button
              variant="outline"
              disabled={isPending}
              onClick={() => logout.mutate()}
              className="rounded-full px-[26px] py-[15px] h-auto border-[#e2e5ef] text-[#3a4256] text-[15px] font-bold hover:bg-[#f6f7fb] shadow-none"
            >
              {logout.isPending ? 'Ending...' : 'End work'}
            </Button>
          </>
        )}
      </div>
      {errorMessage && <p className="text-xs text-red-600 -mt-3">{errorMessage}</p>}

      <div className="self-stretch grid grid-cols-3 gap-2.5 border-t border-[#eceef5] pt-[18px] text-center">
        <div>
          <div className="font-mono text-lg font-medium text-[#0d1220]">{formatClock(brk)}</div>
          <div className="text-[11.5px] text-[#8b93a7] font-semibold mt-0.5">Break time</div>
        </div>
        <div className="border-l border-r border-[#eceef5]">
          <div className="font-mono text-lg font-medium text-[#0d1220]">{clockedInLabel}</div>
          <div className="text-[11.5px] text-[#8b93a7] font-semibold mt-0.5">Clocked in</div>
        </div>
        <div>
          <div className="font-mono text-lg font-medium text-[#0d1220]">
            {formatClock(remainingSeconds)}
          </div>
          <div className="text-[11.5px] text-[#8b93a7] font-semibold mt-0.5">Remaining</div>
        </div>
      </div>
    </div>
  );
}
